import React, { useState, useEffect } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, BarChart3, Activity, Download, AlertCircle, Info, Lock, Crown, RefreshCw, Brain, Zap } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useCountry } from './CountryContext';
import { getCurrencySymbol } from './countryConfig';
import { formatChartValue } from './utils/chartFormatters';
import { calculateBacktest, getAvailableYearRange, validateAllocation } from './utils/portfolio/backtesting';
import { runMonteCarloSimulation, getSampleScenarios, DEFAULT_ASSET_PARAMS } from './utils/portfolio/monteCarlo';
import { analyzeBacktestResults, analyzeMonteCarloResults } from './utils/portfolio/aiAnalysis';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';

// InfoTooltip component for educational information
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="ml-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
      >
        <Info className="w-4 h-4 inline" />
      </button>
      {show && (
        <div className="absolute z-10 w-64 px-3 py-2 text-sm text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2">
          {text}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
            <div className="border-8 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
          </div>
        </div>
      )}
    </div>
  );
};

const PortfolioAnalysis = ({ userProfile, onUpgrade, investments = [] }) => {
  const { currentUser } = useAuth();
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);
  const isPremium = userProfile?.plan === 'premium';

  // Tab state
  const [activeTab, setActiveTab] = useState('backtesting');

  // Default portfolio allocation (includes all asset classes)
  const DEFAULT_ALLOCATIONS = {
    Stocks: 50,
    Bonds: 25,
    'Real Estate': 5,
    Commodities: 5,
    Cryptocurrency: 10,
    Cash: 5
  };

  // Portfolio allocation state
  const [allocations, setAllocations] = useState(DEFAULT_ALLOCATIONS);

  // Backtesting state
  const [startYear, setStartYear] = useState(2014);
  const [endYear, setEndYear] = useState(2024);
  const [initialInvestment, setInitialInvestment] = useState(10000);
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestAnalysis, setBacktestAnalysis] = useState(null);

  // Monte Carlo state
  const [yearsToSimulate, setYearsToSimulate] = useState(30);
  const [numScenarios, setNumScenarios] = useState(10000);
  const [monteCarloResults, setMonteCarloResults] = useState(null);
  const [monteCarloLoading, setMonteCarloLoading] = useState(false);
  const [monteCarloAnalysis, setMonteCarloAnalysis] = useState(null);

  // Persistence state
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Get available year range
  const { minYear, maxYear } = getAvailableYearRange();

  // Calculate allocation from user's actual investments
  const calculateAllocationFromInvestments = () => {
    if (!investments || investments.length === 0) {
      alert('No investments found in your portfolio. Please add investments first.');
      return null;
    }

    // Group by category and sum amounts
    const totalByCategory = investments.reduce((acc, inv) => {
      const category = inv.category || 'Cash';
      const amount = parseFloat(inv.amount || 0);
      acc[category] = (acc[category] || 0) + amount;
      return acc;
    }, {});

    // Calculate total portfolio value
    const totalValue = Object.values(totalByCategory).reduce((sum, val) => sum + val, 0);

    if (totalValue === 0) {
      alert('Your portfolio total value is 0. Please add investment amounts.');
      return null;
    }

    // Convert to percentages matching DEFAULT_ALLOCATIONS structure
    const calculatedAllocations = {};
    Object.keys(DEFAULT_ALLOCATIONS).forEach(assetClass => {
      calculatedAllocations[assetClass] = totalValue > 0
        ? ((totalByCategory[assetClass] || 0) / totalValue) * 100
        : 0;
    });

    return { allocations: calculatedAllocations, totalValue };
  };

  // Auto-populate allocations from portfolio
  const handleAutoPopulate = () => {
    const result = calculateAllocationFromInvestments();
    if (result) {
      setAllocations(result.allocations);
      console.log('Auto-populated allocations from portfolio:', result.allocations);
      console.log('Total portfolio value:', result.totalValue);
    }
  };

  // Load saved portfolio settings from Firebase
  useEffect(() => {
    const loadPortfolioSettings = async () => {
      if (!currentUser || loaded) return;

      try {
        const docRef = doc(db, 'users', currentUser.uid, 'portfolioAnalysis', 'settings');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('Loaded portfolio settings:', data);

          // Load allocations - merge with defaults to include any new asset classes
          if (data.allocations) {
            setAllocations({
              ...DEFAULT_ALLOCATIONS,  // Start with defaults (includes all asset classes)
              ...data.allocations      // Override with saved values
            });
          }

          // Load backtesting settings
          if (data.backtesting) {
            if (data.backtesting.startYear) setStartYear(data.backtesting.startYear);
            if (data.backtesting.endYear) setEndYear(data.backtesting.endYear);
            if (data.backtesting.initialInvestment) setInitialInvestment(data.backtesting.initialInvestment);
          }

          // Load Monte Carlo settings
          if (data.monteCarlo) {
            if (data.monteCarlo.yearsToSimulate) setYearsToSimulate(data.monteCarlo.yearsToSimulate);
            if (data.monteCarlo.numScenarios) setNumScenarios(data.monteCarlo.numScenarios);
          }
        }
        setLoaded(true);
      } catch (error) {
        console.error('Error loading portfolio settings:', error);
        setLoaded(true); // Set loaded even on error to prevent infinite retries
      }
    };

    loadPortfolioSettings();
  }, [currentUser, loaded]);

  // Save portfolio settings to Firebase
  const savePortfolioSettings = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      const docRef = doc(db, 'users', currentUser.uid, 'portfolioAnalysis', 'settings');
      const settingsData = {
        allocations,
        backtesting: {
          startYear,
          endYear,
          initialInvestment
        },
        monteCarlo: {
          yearsToSimulate,
          numScenarios
        },
        lastUpdated: new Date().toISOString()
      };

      await setDoc(docRef, settingsData);
      console.log('Portfolio settings saved successfully');
    } catch (error) {
      console.error('Error saving portfolio settings:', error);
    } finally {
      setSaving(false);
    }
  };

  // Auto-save when allocations or settings change (debounced)
  useEffect(() => {
    if (!loaded) return; // Don't save until initial load is complete

    const timeoutId = setTimeout(() => {
      savePortfolioSettings();
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocations, startYear, endYear, initialInvestment, yearsToSimulate, numScenarios]);

  // Handle allocation change
  const handleAllocationChange = (assetClass, value) => {
    const numValue = parseFloat(value) || 0;
    setAllocations(prev => ({
      ...prev,
      [assetClass]: Math.max(0, Math.min(100, numValue))
    }));
  };

  // Calculate total allocation
  const totalAllocation = Object.values(allocations).reduce((sum, val) => sum + val, 0);
  const isValidAllocation = Math.abs(totalAllocation - 100) < 0.01;

  // Run backtesting
  const runBacktest = () => {
    if (!isPremium) {
      onUpgrade();
      return;
    }

    if (!isValidAllocation) {
      alert('Portfolio allocations must sum to 100%');
      return;
    }

    setBacktestLoading(true);
    try {
      const results = calculateBacktest({
        allocations,
        startYear,
        endYear,
        initialInvestment
      });
      setBacktestResults(results);

      // Generate AI analysis
      const analysis = analyzeBacktestResults(results, allocations);
      setBacktestAnalysis(analysis);
    } catch (error) {
      console.error('Backtesting error:', error);
      alert('Error running backtest: ' + error.message);
    } finally {
      setBacktestLoading(false);
    }
  };

  // Run Monte Carlo simulation
  const runMonteCarlo = () => {
    if (!isPremium) {
      onUpgrade();
      return;
    }

    if (!isValidAllocation) {
      alert('Portfolio allocations must sum to 100%');
      return;
    }

    setMonteCarloLoading(true);
    try {
      const results = runMonteCarloSimulation({
        allocations,
        initialInvestment,
        yearsToSimulate,
        numScenarios
      });
      setMonteCarloResults(results);

      // Generate AI analysis
      const analysis = analyzeMonteCarloResults(results, allocations, initialInvestment);
      setMonteCarloAnalysis(analysis);
    } catch (error) {
      console.error('Monte Carlo error:', error);
      alert('Error running simulation: ' + error.message);
    } finally {
      setMonteCarloLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = (data, filename) => {
    if (!data) return;

    let csvContent = '';

    if (filename.includes('backtest')) {
      csvContent = 'Year,Portfolio Value,Return (%),Gain\n';
      data.timeSeries.forEach(row => {
        csvContent += `${row.year},${row.value.toFixed(2)},${row.return.toFixed(2)},${row.gain.toFixed(2)}\n`;
      });
    } else if (filename.includes('montecarlo')) {
      csvContent = 'Year,5th Percentile,25th Percentile,Median,75th Percentile,95th Percentile\n';
      for (let i = 0; i < data.percentiles.p50.length; i++) {
        csvContent += `${data.percentiles.p50[i].year},${data.percentiles.p5[i].value.toFixed(2)},${data.percentiles.p25[i].value.toFixed(2)},${data.percentiles.p50[i].value.toFixed(2)},${data.percentiles.p75[i].value.toFixed(2)},${data.percentiles.p95[i].value.toFixed(2)}\n`;
      }
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Portfolio Analysis {!isPremium && <Lock className="w-5 h-5 inline text-yellow-500 ml-2" />}</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isPremium ? 'Backtest historical performance and simulate future scenarios' : 'Premium feature - Unlock advanced portfolio analysis'}
            {saving && (
              <span className="ml-2 text-sm text-blue-600 dark:text-blue-400 font-semibold">
                <RefreshCw className="w-3 h-3 inline animate-spin mr-1" />
                Saving...
              </span>
            )}
          </p>
        </div>
        {!isPremium && (
          <button
            onClick={onUpgrade}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-lg hover:from-yellow-500 hover:to-orange-500 transition-all shadow-md"
          >
            <Crown className="w-4 h-4" />
            <span className="font-semibold">Upgrade to Premium</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex space-x-1">
          <button
            onClick={() => setActiveTab('backtesting')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 relative ${
              activeTab === 'backtesting'
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4" />
              <span>Historical Backtesting</span>
              {!isPremium && <Lock className="w-3 h-3" />}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('montecarlo')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 relative ${
              activeTab === 'montecarlo'
                ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Activity className="w-4 h-4" />
              <span>Monte Carlo Simulation</span>
              {!isPremium && <Lock className="w-3 h-3" />}
            </div>
          </button>
        </div>
      </div>

      {/* Portfolio Allocation */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 shadow-sm border border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Portfolio Allocation</h3>
          {investments && investments.length > 0 && (
            <button
              onClick={handleAutoPopulate}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg font-semibold text-sm"
            >
              <Zap className="w-4 h-4" />
              <span>Auto-populate from Portfolio</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(allocations).map(([assetClass, value]) => {
            // Color mapping for gradient backgrounds
            const assetColors = {
              'Stocks': 'from-blue-500 to-blue-600',
              'Bonds': 'from-purple-500 to-purple-600',
              'Real Estate': 'from-green-500 to-green-600',
              'Commodities': 'from-yellow-500 to-yellow-600',
              'Cryptocurrency': 'from-pink-500 to-pink-600',
              'Cash': 'from-orange-500 to-orange-600'
            };

            return (
              <div key={assetClass} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  <div className={`inline-block w-3 h-3 rounded-full bg-gradient-to-r ${assetColors[assetClass]} mr-2`}></div>
                  {assetClass}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={value.toFixed(1)}
                    onChange={(e) => handleAllocationChange(assetClass, e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 font-semibold">%</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className={`text-base font-bold ${isValidAllocation ? 'text-green-600' : 'text-red-600'}`}>
            Total: {totalAllocation.toFixed(1)}% {isValidAllocation ? '✓ Valid' : '✗ Must equal 100%'}
          </div>
          <button
            onClick={() => setAllocations(DEFAULT_ALLOCATIONS)}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-blue-700 hover:text-blue-800 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all font-semibold"
          >
            <RefreshCw className="w-3 h-3" />
            <span>Reset to Default</span>
          </button>
        </div>
      </div>

      {/* Backtesting Tab */}
      {activeTab === 'backtesting' && (
        <div className="space-y-6">
          {!isPremium ? (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl p-8 text-center">
              <Crown className="w-16 h-16 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Premium Feature</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Historical backtesting is available to Premium subscribers. Upgrade to access advanced portfolio analysis with real historical data.
              </p>
              <button
                onClick={onUpgrade}
                className="px-8 py-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-lg hover:from-yellow-500 hover:to-orange-500 transition-all shadow-lg font-semibold"
              >
                Upgrade to Premium
              </button>
            </div>
          ) : (
            <>
              {/* Backtesting Inputs */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Backtesting Parameters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Initial Investment ({currencySymbol})
                </label>
                <input
                  type="number"
                  value={initialInvestment}
                  onChange={(e) => setInitialInvestment(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  min="100"
                  step="1000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Year
                </label>
                <select
                  value={startYear}
                  onChange={(e) => setStartYear(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Year
                </label>
                <select
                  value={endYear}
                  onChange={(e) => setEndYear(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4 flex items-center space-x-4">
              <button
                onClick={runBacktest}
                disabled={!isValidAllocation || backtestLoading}
                className={`px-6 py-3 rounded-lg font-semibold text-white transition-all ${
                  isValidAllocation && !backtestLoading
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {backtestLoading ? (
                  <span className="flex items-center space-x-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Running...</span>
                  </span>
                ) : (
                  'Run Backtest'
                )}
              </button>
              {backtestResults && (
                <button
                  onClick={() => exportToCSV(backtestResults, 'backtest_results.csv')}
                  className="flex items-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
                >
                  <Download className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <strong>Educational Tool:</strong> Historical performance is not a guarantee of future results.
                This backtest uses simplified historical returns for educational purposes only.
              </div>
            </div>
          </div>

          {/* Backtesting Results */}
          {backtestResults && (
            <div className="space-y-6">
              {/* Performance Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                  <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
                  <div className="relative">
                    <p className="text-sm text-green-100 mb-2 font-medium">Final Value</p>
                    <p className="text-3xl font-bold mb-1">
                      {currencySymbol}{backtestResults.finalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                    <p className={`text-sm font-semibold ${backtestResults.totalReturn >= 0 ? 'text-green-100' : 'text-red-200'}`}>
                      {backtestResults.totalReturn >= 0 ? '↗' : '↘'} {backtestResults.totalReturn >= 0 ? '+' : ''}{backtestResults.totalReturn.toFixed(2)}% total
                    </p>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                  <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
                  <div className="relative">
                    <p className="text-sm text-blue-100 mb-2 font-medium">
                      Annualized Return
                      <InfoTooltip text="The average yearly return of your portfolio. This smooths out year-to-year fluctuations to show the compound annual growth rate (CAGR)." />
                    </p>
                    <p className="text-3xl font-bold mb-1">
                      {backtestResults.metrics.annualizedReturn.toFixed(2)}%
                    </p>
                    <p className="text-xs text-blue-100">Per year average</p>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-red-500 to-red-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                  <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
                  <div className="relative">
                    <p className="text-sm text-red-100 mb-2 font-medium">
                      Max Drawdown
                      <InfoTooltip text="The largest peak-to-trough decline in your portfolio value. This shows how much you could have lost during the worst period." />
                    </p>
                    <p className="text-3xl font-bold mb-1">
                      -{backtestResults.metrics.maxDrawdown.toFixed(2)}%
                    </p>
                    <p className="text-xs text-red-100">Largest decline</p>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                  <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
                  <div className="relative">
                    <p className="text-sm text-purple-100 mb-2 font-medium">
                      Volatility
                      <InfoTooltip text="The standard deviation of annual returns. Higher volatility means more unpredictable year-to-year results. Generally, more risk = more volatility." />
                    </p>
                    <p className="text-3xl font-bold mb-1">
                      {backtestResults.metrics.volatility.toFixed(2)}%
                    </p>
                    <p className="text-xs text-purple-100">Annual std dev</p>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Portfolio Growth Over Time</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={backtestResults.timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="year" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" tickFormatter={(value) => formatChartValue(value, currencySymbol)} />
                    <Tooltip
                      formatter={(value) => [`${currencySymbol}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, 'Portfolio Value']}
                      labelFormatter={(label) => `Year ${label}`}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={false} name="Portfolio Value" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* AI Analysis */}
              {backtestAnalysis && (
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center space-x-2 mb-4">
                    <Brain className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">AI Analysis & Insights</h3>
                  </div>

                  {/* Overall Rating */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Overall Rating</p>
                      <p className={`text-2xl font-bold ${
                        backtestAnalysis.overallRating === 'Excellent' ? 'text-green-600' :
                        backtestAnalysis.overallRating === 'Good' ? 'text-blue-600' :
                        backtestAnalysis.overallRating === 'Fair' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {backtestAnalysis.overallRating}
                      </p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk Level</p>
                      <p className={`text-2xl font-bold ${
                        backtestAnalysis.riskLevel === 'High' ? 'text-red-600' :
                        backtestAnalysis.riskLevel === 'Medium' ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {backtestAnalysis.riskLevel}
                      </p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Diversification</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {backtestAnalysis.metrics.diversificationScore}/100
                      </p>
                    </div>
                  </div>

                  {/* Insights */}
                  <div className="space-y-3 mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">Key Insights</h4>
                    {backtestAnalysis.insights.map((insight, index) => (
                      <div key={index} className={`p-4 rounded-lg border-l-4 ${
                        insight.type === 'positive' ? 'bg-green-50 dark:bg-green-900/20 border-green-500' :
                        insight.type === 'negative' ? 'bg-red-50 dark:bg-red-900/20 border-red-500' :
                        insight.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500' :
                        'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                      }`}>
                        <h5 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{insight.title}</h5>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{insight.description}</p>
                      </div>
                    ))}
                  </div>

                  {/* Recommendations */}
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Recommendations</h4>
                    <div className="space-y-3">
                      {backtestAnalysis.recommendations.map((rec, index) => (
                        <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="font-semibold text-gray-900 dark:text-gray-100">{rec.title}</h5>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                              rec.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                              'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            }`}>
                              {rec.priority.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{rec.description}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            <strong>Potential Impact:</strong> {rec.impact}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Monte Carlo Tab */}
      {activeTab === 'montecarlo' && (
        <div className="space-y-6">
          {!isPremium ? (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-xl p-8 text-center">
              <Crown className="w-16 h-16 text-yellow-600 dark:text-yellow-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Premium Feature</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Monte Carlo simulations are available to Premium subscribers. Upgrade to access advanced portfolio analysis.
              </p>
              <button
                onClick={onUpgrade}
                className="px-8 py-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-lg hover:from-yellow-500 hover:to-orange-500 transition-all shadow-lg font-semibold"
              >
                Upgrade to Premium
              </button>
            </div>
          ) : (
            <>
              {/* Monte Carlo Inputs */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Simulation Parameters</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Initial Investment ({currencySymbol})
                    </label>
                    <input
                      type="number"
                      value={initialInvestment}
                      onChange={(e) => setInitialInvestment(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      min="100"
                      step="1000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Years to Simulate
                    </label>
                    <input
                      type="number"
                      value={yearsToSimulate}
                      onChange={(e) => setYearsToSimulate(parseInt(e.target.value) || 1)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      min="1"
                      max="50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Number of Scenarios
                    </label>
                    <select
                      value={numScenarios}
                      onChange={(e) => setNumScenarios(parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="1000">1,000</option>
                      <option value="5000">5,000</option>
                      <option value="10000">10,000 (Recommended)</option>
                      <option value="50000">50,000</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex items-center space-x-4">
                  <button
                    onClick={runMonteCarlo}
                    disabled={!isValidAllocation || monteCarloLoading}
                    className={`px-6 py-3 rounded-lg font-semibold text-white transition-all ${
                      isValidAllocation && !monteCarloLoading
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {monteCarloLoading ? (
                      <span className="flex items-center space-x-2">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Running {numScenarios.toLocaleString()} scenarios...</span>
                      </span>
                    ) : (
                      'Run Simulation'
                    )}
                  </button>
                  {monteCarloResults && (
                    <button
                      onClick={() => exportToCSV(monteCarloResults, 'montecarlo_results.csv')}
                      className="flex items-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
                    >
                      <Download className="w-4 h-4" />
                      <span>Export CSV</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-purple-800">
                    <strong>Simulation Notice:</strong> Monte Carlo simulations show possible outcomes based on
                    statistical assumptions, not predictions. Results are for educational purposes only and do not
                    constitute financial advice.
                  </div>
                </div>
              </div>

              {/* Monte Carlo Results */}
              {monteCarloResults && (
                <div className="space-y-6">
                  {/* Info Banner */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <strong>Understanding Your Results:</strong> Total returns show cumulative growth over {yearsToSimulate} years, while annualized returns show average yearly growth. For example, 850% total = ~7.8% per year over 30 years.
                      </div>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                      <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
                      <div className="relative">
                        <p className="text-sm text-blue-100 mb-2 font-medium">
                          Median Outcome
                          <InfoTooltip text="The middle value of all simulated scenarios. 50% of scenarios are above this value, 50% below. This is the most likely outcome." />
                        </p>
                        <p className="text-3xl font-bold mb-1">
                          {currencySymbol}{monteCarloResults.statistics.median.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </p>
                        <p className={`text-sm font-semibold mb-1 ${monteCarloResults.statistics.medianReturn >= 0 ? 'text-blue-100' : 'text-red-200'}`}>
                          {monteCarloResults.statistics.medianReturn >= 0 ? '+' : ''}{monteCarloResults.statistics.medianReturn.toFixed(1)}% total
                        </p>
                        <p className="text-xs text-blue-100">
                          {((Math.pow(monteCarloResults.statistics.median / initialInvestment, 1 / yearsToSimulate) - 1) * 100).toFixed(2)}% per year
                        </p>
                      </div>
                    </div>

                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                      <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
                      <div className="relative">
                        <p className="text-sm text-green-100 mb-2 font-medium">
                          Best Case (P90)
                          <InfoTooltip text="The 90th percentile outcome. Only 10% of simulated scenarios performed better than this. This represents an optimistic but realistic scenario." />
                        </p>
                        <p className="text-3xl font-bold mb-2">
                          {currencySymbol}{monteCarloResults.statistics.p90.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </p>
                        <p className="text-xs text-green-100">
                          {((Math.pow(monteCarloResults.statistics.p90 / initialInvestment, 1 / yearsToSimulate) - 1) * 100).toFixed(2)}% per year
                        </p>
                      </div>
                    </div>

                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-red-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                      <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
                      <div className="relative">
                        <p className="text-sm text-orange-100 mb-2 font-medium">
                          Worst Case (P10)
                          <InfoTooltip text="The 10th percentile outcome. 90% of simulated scenarios performed better than this. This represents a pessimistic but realistic scenario." />
                        </p>
                        <p className="text-3xl font-bold mb-2">
                          {currencySymbol}{monteCarloResults.statistics.p10.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </p>
                        <p className="text-xs text-orange-100">
                          {((Math.pow(monteCarloResults.statistics.p10 / initialInvestment, 1 / yearsToSimulate) - 1) * 100).toFixed(2)}% per year
                        </p>
                      </div>
                    </div>

                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                      <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
                      <div className="relative">
                        <p className="text-sm text-purple-100 mb-2 font-medium">
                          Risk of Loss
                          <InfoTooltip text="The percentage of simulated scenarios where your final portfolio value is less than your initial investment. This shows the probability of losing money." />
                        </p>
                        <p className="text-3xl font-bold mb-2">
                          {monteCarloResults.statistics.probabilityOfLoss.toFixed(1)}%
                        </p>
                        <p className="text-xs text-purple-100">Below initial investment</p>
                      </div>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Projected Portfolio Range</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <AreaChart data={monteCarloResults.percentiles.p50.map((_, index) => ({
                        year: monteCarloResults.percentiles.p50[index].year,
                        p95: monteCarloResults.percentiles.p95[index].value,
                        p75: monteCarloResults.percentiles.p75[index].value,
                        p50: monteCarloResults.percentiles.p50[index].value,
                        p25: monteCarloResults.percentiles.p25[index].value,
                        p5: monteCarloResults.percentiles.p5[index].value
                      }))}>
                        <defs>
                          <linearGradient id="colorRange" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="year" stroke="#9ca3af" label={{ value: 'Years', position: 'insideBottom', offset: -5 }} />
                        <YAxis stroke="#9ca3af" tickFormatter={(value) => formatChartValue(value, currencySymbol)} />
                        <Tooltip
                          formatter={(value) => `${currencySymbol}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                          labelFormatter={(label) => `Year ${label}`}
                        />
                        <Legend />
                        <Area type="monotone" dataKey="p95" stackId="1" stroke="#10b981" fill="url(#colorRange)" fillOpacity={0.1} name="95th Percentile" />
                        <Area type="monotone" dataKey="p75" stackId="1" stroke="#3b82f6" fill="url(#colorRange)" fillOpacity={0.2} name="75th Percentile" />
                        <Line type="monotone" dataKey="p50" stroke="#8b5cf6" strokeWidth={3} dot={false} name="Median (50th)" />
                        <Area type="monotone" dataKey="p25" stackId="2" stroke="#3b82f6" fill="url(#colorRange)" fillOpacity={0.2} name="25th Percentile" />
                        <Area type="monotone" dataKey="p5" stackId="2" stroke="#ef4444" fill="url(#colorRange)" fillOpacity={0.1} name="5th Percentile" />
                      </AreaChart>
                    </ResponsiveContainer>
                    <div className="mt-4 text-xs text-gray-600 dark:text-gray-400">
                      <p><strong>How to read:</strong> The shaded areas show the range of possible outcomes. The median line (purple) represents the 50th percentile - half of scenarios are above this line, half below.</p>
                    </div>
                  </div>

                  {/* AI Analysis for Monte Carlo */}
                  {monteCarloAnalysis && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center space-x-2 mb-4">
                        <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">AI Analysis & Insights</h3>
                      </div>

                      {/* Confidence Level */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Confidence Level</p>
                          <p className={`text-2xl font-bold ${
                            monteCarloAnalysis.confidenceLevel === 'High' ? 'text-green-600' :
                            monteCarloAnalysis.confidenceLevel === 'Moderate' ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {monteCarloAnalysis.confidenceLevel}
                          </p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Best Case (90th)</p>
                          <p className="text-2xl font-bold text-green-600">
                            {currencySymbol}{monteCarloAnalysis.scenarioAnalysis.bestCase.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Top 10% scenarios</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Worst Case (10th)</p>
                          <p className="text-2xl font-bold text-red-600">
                            {currencySymbol}{monteCarloAnalysis.scenarioAnalysis.worstCase.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Bottom 10% scenarios</p>
                        </div>
                      </div>

                      {/* Insights */}
                      <div className="space-y-3 mb-6">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">Key Insights</h4>
                        {monteCarloAnalysis.insights.map((insight, index) => (
                          <div key={index} className={`p-4 rounded-lg border-l-4 ${
                            insight.type === 'positive' ? 'bg-green-50 dark:bg-green-900/20 border-green-500' :
                            insight.type === 'negative' ? 'bg-red-50 dark:bg-red-900/20 border-red-500' :
                            insight.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500' :
                            insight.type === 'info' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500' :
                            'bg-gray-50 dark:bg-gray-800 border-gray-500'
                          }`}>
                            <h5 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{insight.title}</h5>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{insight.description}</p>
                          </div>
                        ))}
                      </div>

                      {/* Recommendations */}
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Recommendations</h4>
                        <div className="space-y-3">
                          {monteCarloAnalysis.recommendations.map((rec, index) => (
                            <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                              <div className="flex items-start justify-between mb-2">
                                <h5 className="font-semibold text-gray-900 dark:text-gray-100">{rec.title}</h5>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                  rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                                  rec.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                }`}>
                                  {rec.priority.toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{rec.description}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                <strong>Potential Impact:</strong> {rec.impact}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PortfolioAnalysis;
